// Apolo IDE — DispatchStatusBar (Sprint v4 Epic S3 WS S3.4).
//
// Compact horizontal bar showing the overall state of an orchestrated
// dispatch: phase badge, aggregate counts, a thin progress bar, and a
// last-updated stamp. Consumed by SessionWorkspace under the composer.
//
// Pure presentational — state owned by useOrchestrator.

import { Cpu, CheckCircle, XCircle, Loader2, Ban, Clock } from 'lucide-react';

function phaseBadgeClass(phase) {
  switch (phase) {
    case 'running':
      return 'bg-apolo-accent/15 text-apolo-accent';
    case 'completed':
      return 'bg-apolo-success/15 text-apolo-success';
    case 'failed':
      return 'bg-apolo-error/15 text-apolo-error';
    case 'cancelled':
      return 'bg-apolo-warning/15 text-apolo-warning';
    case 'pending':
      return 'bg-apolo-surface2 text-apolo-textTertiary';
    default:
      return 'bg-apolo-surface2 text-apolo-textTertiary';
  }
}

function PhaseIcon({ phase }) {
  if (phase === 'running') return <Loader2 size={11} className="animate-spin" aria-hidden="true" />;
  if (phase === 'completed') return <CheckCircle size={11} aria-hidden="true" />;
  if (phase === 'failed') return <XCircle size={11} aria-hidden="true" />;
  if (phase === 'cancelled') return <Ban size={11} aria-hidden="true" />;
  if (phase === 'pending') return <Clock size={11} aria-hidden="true" />;
  return <Cpu size={11} aria-hidden="true" />;
}

function progressPercent(progress) {
  const total = progress && progress.total ? progress.total : 0;
  if (total <= 0) return 0;
  const done = (progress.completed || 0) + (progress.failed || 0) + (progress.cancelled || 0);
  return Math.min(100, Math.round((done / total) * 100));
}

export default function DispatchStatusBar({ dispatch, progress, phase, busy, error, onKill }) {
  const safePhase = phase || (dispatch && dispatch.status) || 'idle';
  const pct = progressPercent(progress);
  const total = progress && progress.total ? progress.total : 0;
  const id = dispatch && dispatch.id;

  if (!id && safePhase === 'idle') {
    return null;
  }

  return (
    <section
      data-testid="dispatch-status-bar"
      aria-label={`Orchestrator status ${safePhase}`}
      className="rounded-md border border-apolo-borderSubtle bg-apolo-surface1 px-3 py-2 text-[11px]"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span
            data-testid="dispatch-phase-badge"
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${phaseBadgeClass(safePhase)}`}
          >
            <PhaseIcon phase={safePhase} />
            {safePhase}
          </span>
          {id ? (
            <span className="truncate font-mono text-apolo-textTertiary" title={id}>
              {id.slice(0, 8)}
            </span>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-3 text-apolo-textTertiary">
          <span data-testid="dispatch-progress-text">
            <span className="font-mono text-apolo-textPrimary">
              {(progress?.completed || 0) + (progress?.failed || 0) + (progress?.cancelled || 0)}
            </span>
            /
            <span className="font-mono text-apolo-textPrimary">{total}</span>
          </span>
          {onKill && safePhase === 'running' ? (
            <button
              type="button"
              onClick={onKill}
              disabled={busy}
              data-testid="dispatch-kill-inline"
              className="rounded border border-apolo-borderSubtle px-2 py-0.5 text-apolo-error transition hover:bg-apolo-error/10 disabled:opacity-50"
              aria-label="Cancelar dispatch"
            >
              Kill
            </button>
          ) : null}
        </div>
      </div>
      {total > 0 ? (
        <div
          data-testid="dispatch-progress-bar"
          className="mt-2 h-1 overflow-hidden rounded-full bg-apolo-surface2"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={`h-full transition-all ${safePhase === 'failed' ? 'bg-apolo-error' : 'bg-apolo-accent'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      ) : null}
      {error ? (
        <p data-testid="dispatch-error" className="mt-1 text-apolo-error">{error}</p>
      ) : null}
    </section>
  );
}
